import React, { useMemo, useState } from 'react';
import SectionWrapper from '../atoms/SectionWrapper';
import { Table, TableProps } from 'antd';
import RequestDetails from './RequestDetails';
import { useTranslation } from 'react-i18next';
import { useGetBusinessListQuery } from '../../store/services/business-requests';
import dayjs from 'dayjs';
import useQueryParams from '../../hooks/useQueryParams';
import { z } from 'zod';
import { createSearchParams, useLocation, useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, DocumentText, Refresh2 } from 'iconsax-react';
import Button from '../atoms/Button';
import './styles.css';
import { STATUS_COLOR } from '../../constants/system';
import TableWrapper from '../atoms/TableWrapper';
import Filters from './Filters';

type StatusColorKey = keyof typeof STATUS_COLOR;

interface DataType {
  id: string;
  requestId: string;
  applicantName: string;
  mail: string;
  status: string;
  statusCode: string;
  applicantType: string;
  dateApplied: string;
}

interface ProfileType {
  id: string;
  code: string;
  name: string;
}

interface StatusType {
  id: string;
  code: string;
  name: string;
}

interface BusinessListItem {
  id: string;
  requestId: string;
  applicantName: string;
  dateApplied: string;
  email: string;
  profileId: string;
  profileType: ProfileType;
  status: StatusType;
  userId: string;
}

const schema = z
  .object({
    page: z.string().min(1),
    fromDate: z.string().optional(),
    toDate: z.string().optional(),
    statusCode: z.string().optional(),
    profileTypeCode: z.string().optional(),
    searchField: z.string().optional(),
  })
  .strict();

const RequestsTable = () => {
  const [open, setOpen] = useState(false);
  const [selectedRequestId, setSelectedRequestId] = useState<string | null>(
    null
  );
  const navigate = useNavigate();
  const location = useLocation();
  const { ValidQuery, updateQueryParam } = useQueryParams(schema, () => {
    navigate(
      {
        pathname: location.pathname,
        search: createSearchParams({
          page: '0',
        }).toString(),
      },
      { replace: true }
    );
  });

  const {
    t,
    i18n: { language },
  } = useTranslation();
  const queryParams = useMemo(
    () => ({
      ...ValidQuery,
      page: (ValidQuery as { page?: string }).page ?? '0',
      limit: 10,
    }),
    [ValidQuery]
  );

  const {
    data: rawData,
    isFetching,
    isLoading,
    isError,
    refetch,
  } = useGetBusinessListQuery(queryParams, {
    refetchOnMountOrArgChange: true,
  });

  const openDrawer = (requestId: string) => {
    setSelectedRequestId(requestId);
    setOpen(true);
  };

  const closeDrawer = () => {
    setSelectedRequestId(null);
    setOpen(false);
  };

  const columns: TableProps<DataType>['columns'] = useMemo(
    () => [
      {
        title: t('home.requestId'),
        dataIndex: 'requestId',
        key: 'requestId',
        render: (value: string) => (
          <span className="text-gray-600 font-medium">#{value}</span>
        ),
      },
      {
        title: t('home.applicantsName'),
        dataIndex: 'applicantName',
        key: 'applicantName',
      },
      {
        title: t('home.email'),
        dataIndex: 'mail',
        key: 'mail',
        ellipsis: true,
      },
      {
        title: t('home.status'),
        key: 'status',
        dataIndex: 'status',
        render: (value: string, record: DataType) => {
          const statusStyle =
            record.statusCode && record.statusCode in STATUS_COLOR
              ? STATUS_COLOR[record.statusCode as StatusColorKey]
              : { text: '#22C55E', background: '#F0FDF4' };
          return (
            <span
              style={{
                color: statusStyle?.text,
                backgroundColor: statusStyle?.background,
              }}
              className="rounded-md text-center w-fit py-1 px-3 text-sm font-medium"
            >
              {value}
            </span>
          );
        },
      },
      {
        title: t('home.applicantType'),
        key: 'applicantType',
        dataIndex: 'applicantType',
      },
      {
        title: t('home.dateApplied'),
        key: 'dateApplied',
        dataIndex: 'dateApplied',
      },
    ],
    [t, language]
  );

  const data = useMemo(() => {
    dayjs.locale(language);
    return (
      rawData?.payload?.content?.map((item: BusinessListItem) => ({
        id: item.id,
        requestId: item.requestId,
        applicantName: item.applicantName || '-',
        mail: item.email || '-',
        status: item.status?.name || 'New',
        statusCode: item.status?.code?.toUpperCase() || 'NEW',
        applicantType: item.profileType?.name || '-',
        dateApplied: item.dateApplied
          ? dayjs(item.dateApplied).format('DD MMM YYYY')
          : '-',
      })) ?? []
    );
  }, [rawData?.payload?.content, language]);

  const isEmpty = !isLoading && !isError && data.length === 0;

  if (isError) {
    return (
      <SectionWrapper whiteBgChildren>
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-5 p-6 pb-0">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">
              {t('home.applicantsRequests')}
            </h2>
            <p className="text-sm text-gray-500">
              {t('home.applicantsRequestsTableDesc')}
            </p>
          </div>
        </div>
        <div
          className="flex flex-col items-center justify-center py-16 px-4 rounded-xl border border-gray-100 bg-gray-50"
          data-test="requests-table-error"
        >
          <p className="text-gray-600 font-medium mb-2">
            {t('home.somethingWentWrong')}
          </p>
          <p className="text-sm text-gray-500 mb-4 text-center max-w-sm">
            {t('home.applicantsRequestsTableDesc')}
          </p>
          <Button
            type="button"
            variant="outline"
            icon={<Refresh2 size={18} />}
            onClick={() => refetch()}
            data-test="requests-table-retry-btn"
          >
            {t('home.retry')}
          </Button>
        </div>
      </SectionWrapper>
    );
  }

  return (
    <>
      <TableWrapper
        filters={<Filters />}
        title={t('home.applicantsRequests')}
        subTitle={t('home.applicantsRequestsTableDesc')}
      >
        <Table
          rowKey="id"
          rowClassName={(_, index) =>
            `cursor-pointer transition-colors hover:bg-gray-50/80 ${
              index % 2 === 0 ? 'bg-white' : 'bg-gray-100/50'
            }`
          }
          onRow={(record) => ({
            onClick: () => openDrawer(record.id),
          })}
          loading={isFetching || isLoading}
          locale={{
            emptyText: isEmpty ? (
              <div
                className="flex flex-col items-center justify-center py-12 text-gray-500"
                data-test="requests-table-empty"
              >
                <DocumentText size={48} className="mb-3 opacity-50" />
                <p className="font-medium">{t('home.noRequests')}</p>
                <p className="text-sm mt-1">{t('home.noRequestsDesc')}</p>
              </div>
            ) : undefined,
          }}
          pagination={{
            itemRender: (_page, type, originalElement) => {
              if (type === 'prev') {
                return (
                  <a className="flex gap-3 items-center hover:text-black-950">
                    {language === 'ar' ? <ArrowRight /> : <ArrowLeft />}{' '}
                    {t('home.prev')}
                  </a>
                );
              }
              if (type === 'next') {
                return (
                  <a className="flex gap-3 items-center bg-[#F3F4F6] py-1 px-2 rounded-md shadow hover:text-black-950">
                    {t('home.next')}{' '}
                    {language === 'ar' ? <ArrowLeft /> : <ArrowRight />}{' '}
                  </a>
                );
              }
              return originalElement;
            },
            current: Number((ValidQuery as { page?: string })?.page || '0') + 1,
            showSizeChanger: false,
            position: ['bottomCenter'],
            pageSize: 10,
            total: rawData?.payload?.totalElements,
            onChange(page) {
              updateQueryParam([
                { name: 'page', value: String(Number(page) - 1) },
              ]);
            },
          }}
          columns={columns}
          dataSource={data}
        />
        {open && selectedRequestId && (
          <RequestDetails
            requestId={selectedRequestId}
            requestIds={data.map((item: DataType) => item.id)}
            open={open}
            onClose={closeDrawer}
            onRequestIdChange={setSelectedRequestId}
          />
        )}
      </TableWrapper>
    </>
  );
};

export default RequestsTable;
